import { cloneDeep } from 'lodash';
import { IEmailTemplate, ITemplateReconciliationMeta } from '@/typings';

/**
 * Block backfill on open.
 *
 * Blocks gain attributes and data keys over time. A template saved before a key
 * existed opens without it, which renders wrong or crashes the settings panel.
 * This walks the tree and fills in only the keys that are MISSING — an existing
 * value is never overwritten, so the pass cannot lose a user's edit and needs no
 * confirmation from them. It runs on open and again on save, and is idempotent.
 *
 * It used to present a per-block "keep mine / use updated" dialog driven by
 * schema/content version numbers. The numbers were decorative (nothing read them
 * but the dialog), the choice had no consequence (apply never overwrote), and the
 * bookkeeping was keyed by array-index path, so moving any block made the prompt
 * come back forever. All of that is gone — see monday 12868136019.
 */

interface BlockNode {
	type: string;
	data?: {
		value?: Record<string, any>;
	};
	attributes?: Record<string, any>;
	children?: BlockNode[];
}

/** Legacy block types renamed in place. */
const ALIAS_MAP: Record<string, string> = {
	raw: 'html',
};

const LEGACY_STRIPPED_BLOCK_TYPES = new Set(['dynamic_text']);

interface BlockUpgradeDefaults {
	dataValue?: Record<string, any>;
	attributes?: Record<string, any>;
}

const MANAGED_BLOCK_DEFAULTS: Record<string, BlockUpgradeDefaults> = {
	page: {
		dataValue: {
			breakpoint: '480px',
			headAttributes: '',
			'font-size': '14px',
			'font-weight': '400',
			'line-height': '1.7',
			headStyles: [],
			fonts: [],
			responsive: true,
			'font-family': 'Arial, sans-serif',
			'text-color': '#000000',
			'content-background-color': 'transparent',
		},
		attributes: {
			'background-color': '#EDEDEE',
			width: '600px',
		},
	},
	section: {
		attributes: {
			padding: '20px 0px 20px 0px',
			'text-align': 'left',
			'background-color': '#ffffff',
			border: 'none',
			'border-radius': '0px',
		},
	},
	column: {
		attributes: {
			'vertical-align': 'top',
			width: '100%',
		},
	},
	group: {
		attributes: {
			'vertical-align': 'top',
			width: '100%',
		},
	},
	text: {
		dataValue: {
			content: '',
		},
		attributes: {
			padding: '10px 25px 10px 25px',
			align: 'left',
			color: '#000000',
			'font-family': 'Arial, sans-serif',
			'font-size': '13px',
			'font-weight': 'normal',
			'line-height': '22px',
		},
	},
	image: {
		dataValue: {
			imageType: 'static',
			src: '',
			alt: '',
		},
		attributes: {
			padding: '10px 25px 10px 25px',
			align: 'center',
			border: '0',
			target: '_blank',
		},
	},
	divider: {
		attributes: {
			border: 'solid 1px #E0E0E0',
			padding: '10px 25px 10px 25px',
			width: '100%',
			align: 'center',
		},
	},
	spacer: {
		attributes: {
			height: '20px',
			padding: '0px',
		},
	},
	button: {
		dataValue: {
			content: 'Button',
		},
		attributes: {
			align: 'center',
			'background-color': '#414141',
			color: '#ffffff',
			'font-family': 'Arial, sans-serif',
			'font-size': '13px',
			'font-weight': 'normal',
			'line-height': '120%',
			'border-radius': '3px',
			padding: '10px 25px 10px 25px',
			'inner-padding': '10px 25px 10px 25px',
			border: 'none',
			target: '_blank',
		},
	},
	wrapper: {
		attributes: {
			padding: '20px 0px 20px 0px',
			'background-color': '#ffffff',
			border: 'none',
			'border-radius': '0px',
		},
	},
	accordion: {
		attributes: {
			'icon-align': 'middle',
			'icon-width': '32px',
			'icon-height': '32px',
			'icon-wrapped-url': '',
			'icon-wrapped-alt': '+',
			'icon-unwrapped-url': '',
			'icon-unwrapped-alt': '-',
			'icon-position': 'right',
			border: '2px solid black',
			'font-family': 'Arial, sans-serif',
		},
	},
	'accordion-element': {
		attributes: {
			'font-family': 'Arial, sans-serif',
		},
	},
	'accordion-title': {
		dataValue: {
			content: 'Title',
		},
		attributes: {
			padding: '16px',
			'background-color': '#ffffff',
			color: '#000000',
			'font-size': '13px',
			'font-family': 'Arial, sans-serif',
		},
	},
	'accordion-text': {
		dataValue: {
			content: 'Text',
		},
		attributes: {
			padding: '16px',
			'background-color': '#ffffff',
			color: '#000000',
			'font-size': '13px',
			'font-family': 'Arial, sans-serif',
		},
	},
	hero: {
		attributes: {
			mode: 'fluid-height',
			'background-width': 'auto',
			'background-height': 'auto',
			'background-position': 'center center',
			padding: '0px 0px 0px 0px',
			'vertical-align': 'top',
		},
	},
	carousel: {
		attributes: {
			align: 'center',
			'border-radius': '6px',
			'icon-width': '44px',
			'left-icon': '',
			'right-icon': '',
			tb: '10px',
		},
	},
	navbar: {
		attributes: {
			align: 'center',
			'base-url': '',
		},
	},
	social: {
		attributes: {
			mode: 'horizontal',
			align: 'center',
			'border-radius': '3px',
			color: '#333333',
			'font-family': 'Arial, sans-serif',
			'font-size': '13px',
			'line-height': '22px',
			padding: '10px 25px 10px 25px',
			'icon-size': '20px',
			'icon-height': '20px',
		},
	},
	table: {
		dataValue: {
			content: '',
		},
		attributes: {
			padding: '10px 25px 10px 25px',
			align: 'left',
		},
	},
	template: {
		dataValue: {
			content: '',
		},
	},
	html: {
		dataValue: {
			content: '',
		},
		attributes: {
			'background-color': 'transparent',
			padding: '0px 0px 0px 0px',
		},
	},
	listings_block: {
		dataValue: {
			selectedListings: [],
			maxCount: 4,
		},
		attributes: {
			'background-color': 'Transparent',
			'button-text-color': '#ffffff',
			'button-color': '#000000',
			'listing-name-color': '#000000',
			'listing-price-color': '#000000',
			'listing-address-color': '#666666',
			'title-font-size': '16px',
			'price-font-size': '18px',
			'address-font-size': '14px',
			'button-font-size': '14px',
			'button-text': 'More Info',
			'show-title': true,
			'show-address': true,
			'show-price': true,
			'show-details': true,
			layout: 'double',
			padding: '0px',
			title: 'Multiple Listings',
			href: '{{listing.url}}',
			target: '_blank',
		},
	},
	criteria_block: {
		dataValue: {
			maxCount: 4,
			sortBy: 'price_desc',
			criteriaConfig: {},
			criteriaResults: [],
			criteriaResultCount: 0,
		},
		attributes: {
			'background-color': 'Transparent',
			'button-text-color': '#ffffff',
			'button-color': '#000000',
			'listing-name-color': '#000000',
			'listing-price-color': '#000000',
			'listing-address-color': '#666666',
			'title-font-size': '16px',
			'price-font-size': '18px',
			'address-font-size': '14px',
			'button-font-size': '14px',
			'button-text': 'More Info',
			'show-title': true,
			'show-address': true,
			'show-price': true,
			'show-details': true,
			layout: 'double',
			padding: '0px',
			title: 'Criteria',
			href: '{{listing.url}}',
			target: '_blank',
		},
	},
	text_single_listing: {
		dataValue: {
			selectedListingId: '1',
			layout: 'stacked',
		},
		attributes: {
			'background-color': 'Transparent',
			'listing-name-color': '#000000',
			'listing-price-color': '#000000',
			'listing-address-color': '#000000',
			'listing-details-color': '#666666',
			'title-font-size': '16px',
			'price-font-size': '18px',
			'address-font-size': '14px',
			'button-color': '#007bff',
			'button-text-color': '#ffffff',
			'button-font-size': '14px',
			'button-text': 'More Info',
			'show-title': true,
			'show-address': true,
			'show-price': true,
			'show-details': true,
			padding: '0px',
			title: 'Single',
			href: '{{listing.url}}',
			target: '_blank',
		},
	},
	users_block: {
		dataValue: {
			selectedUsers: [],
			maxCount: 6,
		},
		attributes: {
			'background-color': '#ffffff',
			'button-text-color': '#ffffff',
			'button-color': '#007bff',
			'user-name-color': '#000000',
			'user-title-color': '#0066cc',
			'user-address-color': '#666666',
			'user-phone-color': '#666666',
			'user-licence-color': '#666666',
			'title-font-size': '16px',
			'button-text': 'Contact Agent',
			'show-name': true,
			'show-title': true,
			'show-address': true,
			'show-phone': true,
			'show-licence': true,
			layout: 'double',
			padding: '20px',
			title: 'Multiple Users',
		},
	},
	users_criteria_block: {
		dataValue: {
			sourceType: 'criteria',
			maxCount: 6,
			criteriaConfig: {},
			criteriaResults: [],
		},
		attributes: {
			'background-color': '#ffffff',
			'button-text-color': '#ffffff',
			'button-color': '#007bff',
			'user-name-color': '#000000',
			'user-title-color': '#0066cc',
			'user-address-color': '#666666',
			'user-phone-color': '#666666',
			'user-licence-color': '#666666',
			'title-font-size': '16px',
			'button-text': 'Contact Agent',
			'show-name': true,
			'show-title': true,
			'show-address': true,
			'show-phone': true,
			'show-licence': true,
			layout: 'double',
			padding: '20px',
			title: 'Criteria',
		},
	},
	text_single_user: {
		dataValue: {
			selectedUserId: '1',
			layout: 'stacked',
		},
		attributes: {
			'background-color': '#ffffff',
			'user-name-color': '#000000',
			'user-title-color': '#0066cc',
			'user-address-color': '#666666',
			'user-phone-color': '#666666',
			'user-licence-color': '#666666',
			'title-font-size': '16px',
			'name-font-size': '18px',
			'address-font-size': '14px',
			'button-color': '#007bff',
			'button-text-color': '#ffffff',
			'button-font-size': '14px',
			'button-text': 'Contact Agent',
			'show-name': true,
			'show-title': true,
			'show-address': true,
			'show-phone': true,
			'show-licence': true,
			padding: '20px',
			title: 'Single',
		},
	},
	signature_block: {
		dataValue: {
			layout: 'desktop',
		},
		attributes: {
			'background-color': 'transparent',
			'padding-top': '0px',
			'padding-right': '0px',
			'padding-bottom': '0px',
			'padding-left': '0px',
			padding: '0px 0px 0px 0px',
			border: '',
			'border-width': '',
			'border-style': '',
			'border-color': '',
			'border-radius': '0px',
			color: '#000000',
			align: 'left',
		},
	},
	// A true mj-wrapper, so it carries the wrapper defaults plus its listing.
	custom_listing_block: {
		dataValue: {
			selectedListingId: '',
			selectedListing: null,
		},
		attributes: {
			// Structural container — no padding of its own by default.
			padding: '0px 0px 0px 0px',
			border: 'none',
			direction: 'ltr',
			'text-align': 'center',
			'background-repeat': 'no-repeat',
			'background-size': 'cover',
			'background-color': '#FFFFFF',
		},
	},
	dynamic_image: {
		dataValue: {
			imageType: 'dynamic',
			dynamicImageTag: '',
			dynamicImageId: '',
		},
		attributes: {
			align: 'center',
			height: 'auto',
			padding: '0px 0px 0px 0px',
			src: '',
			width: '',
			border: '',
			'border-width': '',
			'border-style': '',
			'border-color': '',
			'container-background-color': 'transparent',
		},
	},
};

type BlockUpgradeHandler = (blockNode: BlockNode) => void;

const MANAGED_BLOCK_UPGRADERS: Record<string, BlockUpgradeHandler> = {
	button: blockNode => {
		if (!blockNode.attributes) {
			blockNode.attributes = {};
		}

		if (
			(blockNode.attributes.target === undefined ||
				blockNode.attributes.target === null) &&
			blockNode.attributes.href
		) {
			blockNode.attributes.target = '_blank';
		}
	},
};

/** Fills absent keys only. Returns true when it wrote something. */
function applyMissingDefaults(
	target: Record<string, any>,
	defaults: Record<string, any>,
) {
	let changed = false;

	Object.entries(defaults).forEach(([key, value]) => {
		if (target[key] === undefined || target[key] === null) {
			// Writing the value already there is not an upgrade — and a default that
			// is itself null never is (null over null/absent backfills nothing).
			// Without this, any block whose defaults carry a null (the Custom
			// Listing Wrapper's selectedListing) re-flagged as "upgraded" on every
			// open and save, so the update banner could never clear.
			if (target[key] !== value && value !== null) {
				changed = true;
			}
			target[key] = value;
		}
	});

	return changed;
}

function upgradeManagedBlock(blockNode: BlockNode, canonicalType: string) {
	const defaults = MANAGED_BLOCK_DEFAULTS[canonicalType];
	const upgradeHandler = MANAGED_BLOCK_UPGRADERS[canonicalType];
	if (!defaults && !upgradeHandler) return false;

	if (!blockNode.data) {
		blockNode.data = { value: {} };
	}

	if (!blockNode.data.value) {
		blockNode.data.value = {};
	}

	if (!blockNode.attributes) {
		blockNode.attributes = {};
	}

	let changed = false;

	if (defaults?.dataValue) {
		changed = applyMissingDefaults(blockNode.data.value, defaults.dataValue) || changed;
	}

	if (defaults?.attributes) {
		changed = applyMissingDefaults(blockNode.attributes, defaults.attributes) || changed;
	}

	if (upgradeHandler) {
		// Handlers rewrite derived values, so snapshot around them rather than
		// asking each one to report back.
		const before = JSON.stringify([blockNode.data.value, blockNode.attributes]);
		upgradeHandler(blockNode);
		changed =
			JSON.stringify([blockNode.data.value, blockNode.attributes]) !== before || changed;
	}

	return changed;
}

/** Human-readable name for the notice — never a raw block type. */
export function getBlockLabel(type: string) {
	switch (type) {
		case 'raw':
		case 'html':
			return 'HTML';
		case 'listings_block':
			return 'Multiple Listings';
		case 'criteria_block':
			return 'Criteria Listings';
		case 'text_single_listing':
			return 'Single Listing';
		case 'custom_listing_block':
			return 'Custom Listing';
		case 'users_block':
			return 'Multiple Users';
		case 'users_criteria_block':
			return 'Criteria Users';
		case 'text_single_user':
			return 'Single User';
		case 'signature_block':
			return 'Signature';
		case 'dynamic_image':
			return 'Dynamic Image';
		default:
			return type
				.split(/[_-]/)
				.filter(Boolean)
				.map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
				.join(' ');
	}
}

/**
 * The editor's empty-container drop hint, as it appears once it has been
 * CAPTURED INTO STORED CONTENT — a text/HTML block that swallowed canvas chrome
 * under an older build. It is not user content and no render guard can suppress
 * it, because by then it is indistinguishable from something the designer typed:
 * it renders on the canvas, in the raster, in the preview and in the sent email
 * alike. The only correct place to remove it is here, where the open-time pass
 * already repairs stored content.
 *
 * Matched on the hint's own SVG shape rather than its copy — a 300-wide viewBox
 * holding a single r="35" circle — so it catches every wording variant (plain,
 * wrapper, listing wrapper, page) and any translation of them.
 */
const CAPTURED_DROP_HINT_SVG = /<svg[^>]*\bwidth="300"[\s\S]*?<\/svg>/gi;
const DROP_HINT_CIRCLE = /\br="35"/i;

function stripCapturedEditorChrome(block: BlockNode) {
	const value = (block as any)?.data?.value;
	if (!value || typeof value !== 'object') return;

	// `content` on a text/html block, `text` on the odd legacy shape.
	(['content', 'text'] as const).forEach(key => {
		const stored = value[key];
		if (typeof stored !== 'string' || !stored.includes('<svg')) return;

		// Only an svg that is BOTH 300 wide and carries the hint's r="35" circle.
		// Either alone is something a designer could plausibly paste; together they
		// are the drop hint and nothing else. Whatever wrapper it sat in is left
		// alone — an empty div renders nothing, and guessing at wrappers is how a
		// cleanup pass starts eating real content.
		const cleaned = stored.replace(CAPTURED_DROP_HINT_SVG, match =>
			DROP_HINT_CIRCLE.test(match) ? '' : match,
		);
		if (cleaned !== stored) value[key] = cleaned;
	});
}

function stripUnsupportedLegacyBlocks(block: BlockNode): BlockNode {
	const nextBlock = block;

	stripCapturedEditorChrome(nextBlock);

	if (!Array.isArray(nextBlock.children)) {
		return nextBlock;
	}

	nextBlock.children = nextBlock.children
		.filter(child => !LEGACY_STRIPPED_BLOCK_TYPES.has(child.type))
		.map(child => stripUnsupportedLegacyBlocks(child));

	return nextBlock;
}

function stripUnsupportedLegacyBlocksFromTemplate(template: IEmailTemplate) {
	if (!template?.content) return;
	stripUnsupportedLegacyBlocks(template.content as unknown as BlockNode);
}

/**
 * Backfill every managed block in place. Returns the labels of the blocks that
 * actually gained something, deduped, for the notice to name.
 */
function upgradeTemplateBlocks(template: IEmailTemplate): string[] {
	const upgraded = new Set<string>();

	const walk = (block: BlockNode) => {
		const aliasType = ALIAS_MAP[block.type];
		if (aliasType) {
			block.type = aliasType;
			upgraded.add(getBlockLabel(aliasType));
		}

		if (upgradeManagedBlock(block, block.type)) {
			upgraded.add(getBlockLabel(block.type));
		}

		if (!Array.isArray(block.children)) return;

		block.children.forEach(child => walk(child));
	};

	if (template?.content) {
		walk(template.content as unknown as BlockNode);
	}

	return [...upgraded];
}

function buildReconciliationMeta(upgradedBlocks: string[]): ITemplateReconciliationMeta {
	return {
		version: 2,
		lastRunAt: new Date().toISOString(),
		upgradedBlocks,
	};
}

/**
 * Run on open. Backfills silently and records what changed so the editor can say
 * so once. Mutates a clone — the caller's template is untouched.
 */
export function initializeTemplateReconciliation(
	template: IEmailTemplate,
): IEmailTemplate {
	const nextTemplate = cloneDeep(template);
	stripUnsupportedLegacyBlocksFromTemplate(nextTemplate);
	nextTemplate.reconciliation = buildReconciliationMeta(
		upgradeTemplateBlocks(nextTemplate),
	);

	return nextTemplate;
}

/**
 * Run on save. Idempotent after initialize — this is the belt-and-braces pass for
 * blocks added during the session from a stale preset.
 */
export function reconcileTemplateForSave(template: IEmailTemplate): IEmailTemplate {
	const nextTemplate = cloneDeep(template);
	stripUnsupportedLegacyBlocksFromTemplate(nextTemplate);
	nextTemplate.reconciliation = buildReconciliationMeta(
		upgradeTemplateBlocks(nextTemplate),
	);

	return nextTemplate;
}
